import React, { useMemo, useState } from 'react'
import {
  ArrowUpIcon,
  ArrowsUpDownIcon,
  CalendarDaysIcon,
  CheckIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TicketIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import {
  EstimatedHoursFilter,
  NO_SOURCE,
  PRIORITY_OPTIONS,
  SortDirection,
  SortField,
  TableSize,
  TicketFilterState,
  UNASSIGNED_ASSIGNEE,
  categoryActiveCount,
} from '../utils/ticketFilterUtils'

interface FilterOption {
  id: string
  label: string
}

interface TicketFiltersModalProps {
  isOpen: boolean
  draft: TicketFilterState
  onChange: (draft: TicketFilterState) => void
  onClose: () => void
  onApply: () => void
  onClearAll: () => void
  matchCount: number
  totalCount: number
  isStaff: boolean
  statusOptions: FilterOption[]
  departmentOptions: FilterOption[]
  sourceOptions: FilterOption[]
  assigneeOptions: FilterOption[]
  organizationOptions: FilterOption[]
  clientUserOptions: FilterOption[]
}

type CategoryId = 'workflow' | 'ticket' | 'dates' | 'people' | 'display'

interface Category {
  id: CategoryId
  label: string
  icon: React.ComponentType<{ className?: string }>
  staffOnly?: boolean
}

const CATEGORIES: Category[] = [
  { id: 'workflow', label: 'Workflow', icon: FunnelIcon },
  { id: 'ticket', label: 'Ticket details', icon: TicketIcon },
  { id: 'dates', label: 'Dates', icon: CalendarDaysIcon },
  { id: 'people', label: 'People', icon: UsersIcon, staffOnly: true },
  { id: 'display', label: 'Display & sort', icon: ArrowsUpDownIcon },
]

const FilterChip: React.FC<{
  label: string
  selected: boolean
  onClick: () => void
}> = ({ label, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center justify-between gap-3 rounded-xl border px-4 py-2.5 text-sm transition-colors ${
      selected
        ? 'border-violet-400 bg-violet-50 text-violet-950'
        : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300 hover:bg-gray-50'
    }`}
  >
    <span className="text-left">{label}</span>
    {selected ? (
      <CheckIcon className="h-4 w-4 shrink-0 text-violet-600" />
    ) : (
      <PlusIcon className="h-4 w-4 shrink-0 text-gray-400" />
    )}
  </button>
)

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mb-8">
    <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</h3>
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">{children}</div>
  </section>
)

const toggleValue = (values: string[], id: string) =>
  values.includes(id) ? values.filter(v => v !== id) : [...values, id]

const TicketFiltersModal: React.FC<TicketFiltersModalProps> = ({
  isOpen,
  draft,
  onChange,
  onClose,
  onApply,
  onClearAll,
  matchCount,
  totalCount,
  isStaff,
  statusOptions,
  departmentOptions,
  sourceOptions,
  assigneeOptions,
  organizationOptions,
  clientUserOptions,
}) => {
  const [activeCategory, setActiveCategory] = useState<CategoryId>('workflow')
  const [sidebarSearch, setSidebarSearch] = useState('')
  const [hideInactive, setHideInactive] = useState(false)

  const visibleCategories = useMemo(() => {
    return CATEGORIES.filter(category => {
      if (category.staffOnly && !isStaff) return false
      if (!sidebarSearch.trim()) {
        if (!hideInactive) return true
        return categoryActiveCount(category.id, draft, isStaff) > 0 || category.id === activeCategory
      }
      return category.label.toLowerCase().includes(sidebarSearch.trim().toLowerCase())
    })
  }, [activeCategory, draft, hideInactive, isStaff, sidebarSearch])

  if (!isOpen) return null

  const updateDraft = (partial: Partial<TicketFilterState>) => onChange({ ...draft, ...partial })

  const renderWorkflow = () => (
    <>
      <Section title="Status">
        {statusOptions.map(option => (
          <FilterChip
            key={option.id}
            label={option.label}
            selected={draft.selectedStatuses.includes(option.id)}
            onClick={() =>
              updateDraft({ selectedStatuses: toggleValue(draft.selectedStatuses, option.id) })
            }
          />
        ))}
      </Section>
      <Section title="Priority">
        {PRIORITY_OPTIONS.map(priority => (
          <FilterChip
            key={priority}
            label={priority === 'untracked' ? 'Untracked' : priority.charAt(0).toUpperCase() + priority.slice(1)}
            selected={draft.selectedPriorities.includes(priority)}
            onClick={() =>
              updateDraft({ selectedPriorities: toggleValue(draft.selectedPriorities, priority) })
            }
          />
        ))}
      </Section>
    </>
  )

  const renderTicket = () => (
    <>
      <Section title="Department">
        {departmentOptions.map(option => (
          <FilterChip
            key={option.id}
            label={option.label}
            selected={draft.selectedDepartments.includes(option.id)}
            onClick={() =>
              updateDraft({ selectedDepartments: toggleValue(draft.selectedDepartments, option.id) })
            }
          />
        ))}
      </Section>
      <Section title="Source">
        <FilterChip
          label="No source"
          selected={draft.selectedSources.includes(NO_SOURCE)}
          onClick={() =>
            updateDraft({ selectedSources: toggleValue(draft.selectedSources, NO_SOURCE) })
          }
        />
        {sourceOptions.map(option => (
          <FilterChip
            key={option.id}
            label={option.label}
            selected={draft.selectedSources.includes(option.id)}
            onClick={() =>
              updateDraft({ selectedSources: toggleValue(draft.selectedSources, option.id) })
            }
          />
        ))}
      </Section>
      <section className="mb-8">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Tags</h3>
        <input
          type="text"
          className="input-field w-full max-w-md text-sm"
          placeholder="Contains tag..."
          value={draft.tagSearch}
          onChange={(e) => updateDraft({ tagSearch: e.target.value })}
        />
      </section>
      <Section title="Estimated hours">
        {([
          ['all', 'All tickets'],
          ['has', 'Has estimate'],
          ['none', 'No estimate'],
        ] as const).map(([value, label]) => (
          <FilterChip
            key={value}
            label={label}
            selected={draft.estimatedHoursFilter === value}
            onClick={() => updateDraft({ estimatedHoursFilter: value as EstimatedHoursFilter })}
          />
        ))}
      </Section>
    </>
  )

  const renderDates = () => (
    <section className="mb-8 max-w-xl">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Created date</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="filter-created-from" className="mb-1 block text-sm font-medium text-gray-700">
            Start date
          </label>
          <input
            id="filter-created-from"
            type="date"
            className="input-field w-full text-sm"
            value={draft.createdFrom}
            onChange={(e) => updateDraft({ createdFrom: e.target.value })}
          />
        </div>
        <div>
          <label htmlFor="filter-created-to" className="mb-1 block text-sm font-medium text-gray-700">
            End date
          </label>
          <input
            id="filter-created-to"
            type="date"
            className="input-field w-full text-sm"
            value={draft.createdTo}
            min={draft.createdFrom || undefined}
            onChange={(e) => updateDraft({ createdTo: e.target.value })}
          />
        </div>
      </div>
    </section>
  )

  const renderPeople = () => (
    <>
      <Section title="Assigned to">
        <FilterChip
          label="Unassigned"
          selected={draft.selectedAssignees.includes(UNASSIGNED_ASSIGNEE)}
          onClick={() =>
            updateDraft({ selectedAssignees: toggleValue(draft.selectedAssignees, UNASSIGNED_ASSIGNEE) })
          }
        />
        {assigneeOptions.map(option => (
          <FilterChip
            key={option.id}
            label={option.label}
            selected={draft.selectedAssignees.includes(option.id)}
            onClick={() =>
              updateDraft({ selectedAssignees: toggleValue(draft.selectedAssignees, option.id) })
            }
          />
        ))}
      </Section>
      <Section title="Client organization">
        {organizationOptions.map(option => (
          <FilterChip
            key={option.id}
            label={option.label}
            selected={draft.selectedClientOrgs.includes(option.id)}
            onClick={() => {
              const nextOrgs = toggleValue(draft.selectedClientOrgs, option.id)
              updateDraft({
                selectedClientOrgs: nextOrgs,
                selectedClientUsers: nextOrgs.length === 1 ? draft.selectedClientUsers : [],
              })
            }}
          />
        ))}
      </Section>
      <Section title="Client user">
        {draft.selectedClientOrgs.length !== 1 ? (
          <p className="col-span-full text-sm text-gray-500">
            Select exactly one client organization to filter by user.
          </p>
        ) : clientUserOptions.length === 0 ? (
          <p className="col-span-full text-sm text-gray-500">No users found for this organization.</p>
        ) : (
          clientUserOptions.map(option => (
            <FilterChip
              key={option.id}
              label={option.label}
              selected={draft.selectedClientUsers.includes(option.id)}
              onClick={() =>
                updateDraft({ selectedClientUsers: toggleValue(draft.selectedClientUsers, option.id) })
              }
            />
          ))
        )}
      </Section>
    </>
  )

  const renderDisplay = () => (
    <>
      <Section title="Sort by">
        {([
          ['createdAt', 'Created date'],
          ['title', 'Title'],
          ['priority', 'Priority'],
          ['status', 'Status'],
        ] as const).map(([field, label]) => (
          <FilterChip
            key={field}
            label={label}
            selected={draft.sortField === field}
            onClick={() => updateDraft({ sortField: field as SortField })}
          />
        ))}
      </Section>
      <Section title="Sort direction">
        <FilterChip
          label="Newest / A → Z first"
          selected={draft.sortDirection === 'desc'}
          onClick={() => updateDraft({ sortDirection: 'desc' as SortDirection })}
        />
        <FilterChip
          label="Oldest / Z → A first"
          selected={draft.sortDirection === 'asc'}
          onClick={() => updateDraft({ sortDirection: 'asc' as SortDirection })}
        />
      </Section>
      <Section title="Table size">
        {([
          ['small', 'Small — compact rows'],
          ['medium', 'Medium — default'],
          ['large', 'Large — spacious rows'],
        ] as const).map(([size, label]) => (
          <FilterChip
            key={size}
            label={label}
            selected={draft.tableSize === size}
            onClick={() => updateDraft({ tableSize: size as TableSize })}
          />
        ))}
      </Section>
    </>
  )

  const renderCategoryContent = () => {
    switch (activeCategory) {
      case 'workflow':
        return renderWorkflow()
      case 'ticket':
        return renderTicket()
      case 'dates':
        return renderDates()
      case 'people':
        return renderPeople()
      case 'display':
        return renderDisplay()
      default:
        return null
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="flex h-[min(85vh,820px)] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Close filters"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
            <h2 className="text-lg font-semibold text-gray-900">Filters &amp; sorting</h2>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-gray-500 sm:inline">
              {matchCount} of {totalCount} tickets
            </span>
            <button type="button" className="btn-secondary text-xs" onClick={onClearAll}>
              Clear all
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700"
              onClick={onApply}
            >
              Save changes
              <ArrowUpIcon className="h-4 w-4 rotate-45" />
            </button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1">
          <aside className="flex w-56 shrink-0 flex-col border-r border-gray-200 bg-gray-50/80">
            <div className="border-b border-gray-200 p-3">
              <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  className="input-field w-full pl-9 text-xs"
                  placeholder="Search filters"
                  value={sidebarSearch}
                  onChange={(e) => setSidebarSearch(e.target.value)}
                />
              </div>
            </div>

            <nav className="flex-1 overflow-y-auto p-2">
              {visibleCategories.map(category => {
                const Icon = category.icon
                const activeCount = categoryActiveCount(category.id, draft, isStaff)
                const isActive = activeCategory === category.id
                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => setActiveCategory(category.id)}
                    className={`mb-1 flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${
                      isActive
                        ? 'bg-violet-100 text-violet-900'
                        : 'text-gray-700 hover:bg-white hover:text-gray-900'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <Icon className={`h-4 w-4 ${isActive ? 'text-violet-600' : 'text-gray-400'}`} />
                      {category.label}
                    </span>
                    {activeCount > 0 && (
                      <span className="rounded-full bg-violet-600 px-2 py-0.5 text-[10px] font-semibold text-white">
                        {activeCount}
                      </span>
                    )}
                  </button>
                )
              })}
            </nav>

            <div className="space-y-2 border-t border-gray-200 p-3">
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={hideInactive}
                  onChange={(e) => setHideInactive(e.target.checked)}
                  className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                />
                Hide inactive filters
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={draft.showActiveFilterPills}
                  onChange={(e) => updateDraft({ showActiveFilterPills: e.target.checked })}
                  className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                />
                Show filter pills
              </label>
            </div>
          </aside>

          <div className="min-w-0 flex-1 overflow-y-auto px-6 py-5">{renderCategoryContent()}</div>
        </div>
      </div>
    </div>
  )
}

export default TicketFiltersModal
